import { ReactNode, useEffect } from 'react';
import { Box, Container } from '@mui/material';
import SelectLanguage from '@shared/components/common/SelectLanguage';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useActorData } from '@shared/components/common/useAppData';
import { ThemeSwitcher } from '@toolpad/core/DashboardLayout';
import { AppProvider } from '@toolpad/core';
import { useCaptivePortalData } from '@device/routes/captive';
import { FillContentLoader } from '@shared/components/Loader';

type Props = {
  noAuth?: boolean;
  children: ReactNode | ReactNode[];
};

export function DeviceDashboard({ children, noAuth }: Props) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const actorData = useActorData();
  const captivePortal = useCaptivePortalData();

  useEffect(() => {
    if (noAuth) {
      return;
    }
    if (actorData.isLoading || captivePortal.isLoading) {
      return;
    }
    if (actorData.isSuccess) {
      return;
    }
    if (captivePortal.isSuccess) {
      navigate('/captive');
      return;
    }
    if (!!actorData.error) {
      navigate('/activate');
      return;
    }
    if (actorData.data?.actor?.actorType !== 'InternetDevice') {
      navigate('/403');
      return;
    }
  }, [
    actorData.isLoading,
    actorData.data,
    actorData.isError,
    noAuth,
    navigate,
    actorData.error,
    captivePortal.isLoading,
    captivePortal.error,
  ]);

  const accessGranted = noAuth || actorData.data?.actor?.actorType === 'InternetDevice';

  if (!accessGranted) {
    return (
      <Container
        component="main"
        maxWidth="xl"
        sx={{
          m: 0,
          pb: 0,
          height: '100vh',
        }}
      >
        <FillContentLoader />
      </Container>
    );
  }

  return (
    <AppProvider>
      <Container
        component="main"
        maxWidth="xl"
        sx={{
          m: 0,
          pb: 0,
        }}
      >
        <Box
          sx={{
            m: 0,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'right',
          }}
        >
          <Box
            sx={{
              pt: 1,
              mr: 1,
            }}
          >
            <ThemeSwitcher />
          </Box>
          <Box sx={{ pt: 1 }}>
            <SelectLanguage
              margin="normal"
              name="language"
              variant="icon"
              value={i18n.language}
              onChange={(event) => i18n.changeLanguage(event.target.value)}
            />
          </Box>
        </Box>
      </Container>
      <Container component="main" maxWidth="md">
        {children}
      </Container>
    </AppProvider>
  );
}
